import React, { useState, useEffect } from 'react';
import { documentAPI, employeeAPI } from '../../services/api';
import { formatDateForInput, isValidDate } from '../../utils/helpers';
import { DOCUMENT_TYPES, DOCUMENT_STATUSES, VALIDATION_MESSAGES, ERROR_MESSAGES } from '../../utils/constants';

const DocumentForm = ({ document, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    doc_type: '',
    doc_id: '',
    uri: '',
    pdf_path: '',
    xml_metadata_path: '',
    issue_date: '',
    expiry_date: '',
    status: DOCUMENT_STATUSES.ACTIVE,
  });

  const [employees, setEmployees] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchEmployees();
    
    if (document) {
      setFormData({
        employee_id: document.employee_id || '',
        doc_type: document.doc_type || '',
        doc_id: document.doc_id || '',
        uri: document.uri || '',
        pdf_path: document.pdf_path || '',
        xml_metadata_path: document.xml_metadata_path || '',
        issue_date: formatDateForInput(document.issue_date) || '',
        expiry_date: formatDateForInput(document.expiry_date) || '',
        status: document.status || DOCUMENT_STATUSES.ACTIVE,
      });
    }
  }, [document]);

  // Fetch employees for dropdown
  const fetchEmployees = async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.data.data || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.employee_id) {
      newErrors.employee_id = VALIDATION_MESSAGES.REQUIRED;
    }

    if (!formData.doc_type) {
      newErrors.doc_type = VALIDATION_MESSAGES.REQUIRED;
    }

    if (!formData.doc_id.trim()) {
      newErrors.doc_id = VALIDATION_MESSAGES.REQUIRED;
    }

    if (!formData.uri.trim()) {
      newErrors.uri = VALIDATION_MESSAGES.REQUIRED;
    }

    if (!formData.pdf_path.trim()) {
      newErrors.pdf_path = VALIDATION_MESSAGES.REQUIRED;
    }

    if (!formData.xml_metadata_path.trim()) {
      newErrors.xml_metadata_path = VALIDATION_MESSAGES.REQUIRED;
    }

    if (!formData.issue_date) {
      newErrors.issue_date = VALIDATION_MESSAGES.REQUIRED;
    } else if (!isValidDate(formData.issue_date)) {
      newErrors.issue_date = VALIDATION_MESSAGES.INVALID_DATE;
    }

    if (formData.expiry_date && !isValidDate(formData.expiry_date)) {
      newErrors.expiry_date = VALIDATION_MESSAGES.INVALID_DATE;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Auto-generate URI when doc_type and doc_id change
  useEffect(() => {
    if (formData.doc_type && formData.doc_id) {
      const uri = `in.gov.maharashtrasrpf-${formData.doc_type}-${formData.doc_id}`;
      setFormData(prev => ({
        ...prev,
        uri: uri
      }));
    }
  }, [formData.doc_type, formData.doc_id]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (document) {
        await documentAPI.update(document.doc_id, formData);
      } else {
        await documentAPI.create(formData);
      }

      onSubmit();
    } catch (err) {
      console.error('Error saving document:', err);
      if (err.response?.data?.error?.includes('already exists')) {
        setErrors({ doc_id: VALIDATION_MESSAGES.DUPLICATE_DOCUMENT_ID });
      } else {
        setErrors({ general: ERROR_MESSAGES.NETWORK_ERROR });
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF/XML from employee
  const handleGenerate = async () => {
    // Basic input checks
    const required = ['employee_id', 'doc_type', 'doc_id', 'issue_date'];
    for (const k of required) {
      if (!formData[k] || String(formData[k]).trim() === '') {
        setErrors(prev => ({ ...prev, [k]: VALIDATION_MESSAGES.REQUIRED }));
        return;
      }
    }
    const uri = formData.uri?.trim() || `in.gov.maharashtrasrpf-${formData.doc_type}-${formData.doc_id}`;
    try {
      setGenerating(true);
      const payload = {
        employee_id: formData.employee_id,
        doc_type: formData.doc_type,
        doc_id: formData.doc_id,
        uri,
        issue_date: formData.issue_date,
        expiry_date: formData.expiry_date || undefined,
        status: formData.status,
      };
      const resp = await documentAPI.generateFromEmployee(payload);
      const doc = resp?.data?.data;
      if (doc) {
        setFormData(prev => ({
          ...prev,
          uri: doc.uri || uri,
          pdf_path: doc.pdf_path || prev.pdf_path,
          xml_metadata_path: doc.xml_metadata_path || prev.xml_metadata_path,
        }));
      }
    } catch (e) {
      console.error('Generate from employee failed:', e);
      setErrors(prev => ({ ...prev, general: ERROR_MESSAGES.NETWORK_ERROR }));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{document ? 'Edit Document' : 'Add New Document'}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="document-form">
          {errors.general && (
            <div className="alert alert-danger">{errors.general}</div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="employee_id">Employee *</label>
              <select
                id="employee_id"
                name="employee_id"
                value={formData.employee_id}
                onChange={handleChange}
                className={`form-control ${errors.employee_id ? 'is-invalid' : ''}`}
              >
                <option value="">Select Employee</option>
                {employees.map(employee => (
                  <option key={employee.employee_id} value={employee.employee_id}>
                    {employee.full_name} ({employee.employee_id})
                  </option>
                ))}
              </select>
              {errors.employee_id && <div className="invalid-feedback">{errors.employee_id}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="doc_type">Document Type *</label>
              <select
                id="doc_type"
                name="doc_type"
                value={formData.doc_type}
                onChange={handleChange}
                className={`form-control ${errors.doc_type ? 'is-invalid' : ''}`}
              >
                <option value="">Select Document Type</option>
                {Object.entries(DOCUMENT_TYPES).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
              {errors.doc_type && <div className="invalid-feedback">{errors.doc_type}</div>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="doc_id">Document ID *</label>
              <input
                type="text"
                id="doc_id"
                name="doc_id"
                value={formData.doc_id}
                onChange={handleChange}
                className={`form-control ${errors.doc_id ? 'is-invalid' : ''}`}
                placeholder="e.g., X8F3A2B"
                disabled={!!document}
              />
              {errors.doc_id && <div className="invalid-feedback">{errors.doc_id}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-control"
              >
                {Object.entries(DOCUMENT_STATUSES).map(([key, value]) => (
                  <option key={key} value={value}>{value}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="uri">URI *</label>
            <input
              type="text"
              id="uri"
              name="uri"
              value={formData.uri}
              onChange={handleChange}
              className={`form-control ${errors.uri ? 'is-invalid' : ''}`}
              placeholder="in.gov.maharashtrasrpf-IDCRD-X8F3A2B"
            />
            {errors.uri && <div className="invalid-feedback">{errors.uri}</div>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="pdf_path">PDF Path *</label>
              <input
                type="text"
                id="pdf_path"
                name="pdf_path"
                value={formData.pdf_path}
                onChange={handleChange}
                className={`form-control ${errors.pdf_path ? 'is-invalid' : ''}`}
                placeholder="./files/idcards/101.pdf"
              />
              {errors.pdf_path && <div className="invalid-feedback">{errors.pdf_path}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="xml_metadata_path">XML Metadata Path *</label>
              <input
                type="text"
                id="xml_metadata_path"
                name="xml_metadata_path"
                value={formData.xml_metadata_path}
                onChange={handleChange}
                className={`form-control ${errors.xml_metadata_path ? 'is-invalid' : ''}`}
                placeholder="./files/idcards/101.xml"
              />
              {errors.xml_metadata_path && <div className="invalid-feedback">{errors.xml_metadata_path}</div>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="issue_date">Issue Date *</label>
              <input
                type="date"
                id="issue_date"
                name="issue_date"
                value={formData.issue_date}
                onChange={handleChange}
                className={`form-control ${errors.issue_date ? 'is-invalid' : ''}`}
              />
              {errors.issue_date && <div className="invalid-feedback">{errors.issue_date}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="expiry_date">Expiry Date</label>
              <input
                type="date"
                id="expiry_date"
                name="expiry_date"
                value={formData.expiry_date}
                onChange={handleChange}
                className={`form-control ${errors.expiry_date ? 'is-invalid' : ''}`}
              />
              {errors.expiry_date && <div className="invalid-feedback">{errors.expiry_date}</div>}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-outline-primary " onClick={handleGenerate} disabled={generating} 
                  style={{
                    background: generating ? '#f3f4f6' : 'white',
                    borderColor: '#3b82f6',
                    color: '#3b82f6'
                  }}>
             {generating ? 'Generating...' : (
                    <>
                     <i class="fa-solid fa-file-lines"></i> Generate Document
                    </>
              )}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (document ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentForm;
